// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/**
 * The kinds of incident a record may be. Sprint 65.9.0 turned this from a type
 * alias into a runtime array, for the reason 65.8.0 found five times: a type
 * alias has nothing importable, so a Zod schema can only RESTATE it, and a
 * restated vocabulary is one free to drift from the type it claims to mirror.
 *
 * It had drifted. `CreateIncidentSchema` accepted `z.string().optional()`, so
 * the API took any string at all into a field the type says holds one of eight
 * values — and seven of the eight were written by no backend path.
 */
export const INCIDENT_CATEGORIES = [
    'data_breach',
    'unauthorized_access',
    'data_loss',
    'system_compromise',
    'human_error',
    'physical_security',
    'vendor_incident',
    /**
     * Sprint 62.48.0, wave 2 (AC17) — an incident in an AI system's operation (Điều 9).
     * The value names a platform activity: the link to the AI system profile is
     * `ai_profile_id`, and the serious-incident record is held by ai-governance.
     */
    'ai_system',
    'other',
];
/**
 * The determination an incident triage records: is this a personal-data breach?
 *
 * Sprint 65.15.0 — this was a bare union, and `IncidentDetailPage` carried its own
 * `BREACH_DETERMINATION_KEYS = ['yes','no','uncertain']` literal beside it. Two
 * lists of the same three strings is one list too many, and the locale map was a
 * THIRD, keyed `confirmed_breach` / `suspected_breach` / `not_a_breach` /
 * `pending` — which intersected neither. Every option therefore missed its label
 * and fell back to a title-cased English key, in all sixteen locales.
 *
 * The array is the declaration and the type derives from it, so a page that
 * renders the options cannot enumerate a different set, and
 * `incident-breach-determination-labels.test.ts` holds the locale maps to it.
 */
export const BREACH_DETERMINATIONS = ['yes', 'no', 'uncertain'];
// ── Impact Assessment ──────────────────────────────────────
/**
 * The data categories an impact assessment records, array first and the type
 * derived, so the prefill registry and the sample-data validator read the one
 * declaration instead of restating it.
 *
 * Sprint 63.37.0 (legal second read S12) — the vocabulary is deliberately
 * coarse. Sample content had recorded field-level codes here (`salary`,
 * `tax_id`, `national_id`, …) that are not members, and the prefill engine
 * printed them on an official form as answers. Engineering ruling: the content
 * uses these members, and the detail a code carried (which fields of which
 * record) is written in a person's words — the incident description or a
 * free-text field of the form. The union does not grow.
 */
export const DATA_CATEGORIES_AFFECTED = [
    'personal_data',
    'sensitive_personal_data',
    'financial_data',
    'health_data',
    'childrens_data',
    'other',
];
/** Whether a stored or received value is one of the declared data categories. */
export function isDataCategoryAffected(value) {
    return (typeof value === 'string' && DATA_CATEGORIES_AFFECTED.includes(value));
}
/**
 * The potential consequences the incident page offers, array first and the
 * type derived (Sprint 63.37.0, legal second read S12). The page wrote these
 * codes and printed them title-cased in English in every locale; the prefill
 * registry now reads the same declaration to name them. Sample content may
 * still hold a person's own words in the field, which are shown as written.
 */
export const POTENTIAL_CONSEQUENCES = [
    'identity_theft',
    'financial_loss',
    'reputational_damage',
    'discrimination',
    'emotional_distress',
    'physical_harm',
    'loss_of_confidentiality',
    'loss_of_access',
];
// ── Breach Assessment ──────────────────────────────────────
/**
 * A notification requirement's status members, array first and the type
 * derived, so the notification-status route validates against this
 * declaration instead of restating it. Generic workflow words (R-46).
 *
 * Sprint 62.35.0 gate E12.5 pass 3 (idea-catalog #368, its status half) — the
 * route took `z.string().min(1)`, so any string was stored as a status.
 */
export const NOTIFICATION_STATUSES = [
    'not_required',
    'required',
    'in_progress',
    'completed',
    'overdue',
];
/**
 * The error code of the 409 an incident requirement write returns when the
 * requirement revision it names is no longer the stored one. The server
 * raises it and the page branches on it, from this one declaration: a page
 * that matched any 409 would treat an unrelated conflict as this one
 * (gate E12.5 pass 3).
 */
export const INCIDENT_REQUIREMENTS_CHANGED = 'INCIDENT_REQUIREMENTS_CHANGED';
/** Whether a stored or received value is one of the declared members. */
export function isNotificationStatus(value) {
    return typeof value === 'string' && NOTIFICATION_STATUSES.includes(value);
}
/**
 * Sprint 62.35.0 (Slice E, AC9) — who, beyond the data subjects, an incident's
 * notification reached.
 *
 * The data-sector sanctions instruments name "tổ chức, cá nhân có liên quan"
 * (the related organisations and individuals) as parties to be informed, not
 * only data subjects and the authority. `other_parties: string[]` could name
 * them but recorded neither WHEN each was told nor WHETHER it was, so a list of
 * names was indistinguishable from a list of notifications.
 *
 * These are generic role words, not instrument vocabulary (R-46): a data owner,
 * a partner, the recipient of a handover. Arrays first and types derived, so the
 * route validator reads the same declaration instead of restating it.
 */
export const AFFECTED_PARTY_TYPES = [
    'data_owner',
    'partner',
    'handover_recipient',
    'other',
];
export const AFFECTED_PARTY_NOTIFICATION_STATUSES = [
    'pending',
    'notified',
    'not_required',
];
// ── Sprint 48.3.14: Forseti Mẫu số 08 field draft ──────────
/**
 * The fields the Forseti breach-notification draft fills, named by the prefill
 * source each one carries (`FORM_PREFILL_SOURCES`).
 *
 * Sprint 63.37.0 (E4) — these were six `field_id`s that matched the official
 * form's own field ids by name only, so a pack that renamed a field silently
 * moved the draft off it. The draft now finds each target as the template
 * field whose `prefill.source` is the key; a key no field declares is not
 * drafted and is reported in `undeclared_fields`.
 */
export const BREACH_NOTIFICATION_DRAFT_FIELD_KEYS = [
    'incident.description',
    'incident.data_categories',
    'incident.subjects_affected',
    'incident.consequences',
    'incident.containment_measures',
    'incident.remediation_measures',
];
/** Sprint 62.49.0 gate E12.5 pass 2 (P2-4) — the shortest reason a receipt correction accepts. */
export const AUTHORITY_REQUEST_RECEIPT_REASON_MIN_LENGTH = 10;
//# sourceMappingURL=incident.js.map